import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { TranscriptEntry, getTranscriptStats } from '../store/conversationSlice';

interface TranscriptFeedProps {
  isCustomerSpeaking?: boolean;
}

const TypingIndicator: React.FC = () => (
  <div className="flex justify-start mb-1">
    <div className="bg-gray-100 rounded-lg px-2 py-1">
      <div className="flex items-center space-x-1">
        <span className="text-xs text-gray-500 mr-1">Speaking</span>
        <div className="flex space-x-0.5">
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  </div>
);

const TranscriptFeed: React.FC<TranscriptFeedProps> = ({ isCustomerSpeaking = false }) => {
  const { transcripts } = useSelector((state: RootState) => state.conversation);
  const feedRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Auto-scroll to bottom when new transcripts are added
  useEffect(() => {
    if (feedRef.current && transcripts.length > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (feedRef.current) {
          feedRef.current.scrollTop = feedRef.current.scrollHeight;
        }
      });
    }
  }, [transcripts]);

  const formatTime = (timestamp: string) => {
    try {

      // If timestamp doesn't have timezone info, treat it as UTC by appending 'Z'
      let timestampWithTz = timestamp;
      if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
        timestampWithTz = timestamp + 'Z';
      }

      const date = new Date(timestampWithTz);
      // Convert UTC to local time and format in 12-hour format
      const formatted = date.toLocaleTimeString(undefined, {
        hour12: true,  // Use 12-hour format with AM/PM
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit'
      });
      return formatted;
    } catch {
      return 'Invalid time';
    }
  };

  const TranscriptMessage: React.FC<{ transcript: TranscriptEntry }> = ({ transcript }) => {
    const isCustomer = transcript.speaker === 'customer';

    return (
      <div className={`flex ${isCustomer ? 'justify-start' : 'justify-end'} mb-1`}>
        <div className={`max-w-xs lg:max-w-md px-2 py-1 rounded-lg ${
          isCustomer
            ? 'bg-gray-100 text-gray-800'
            : 'bg-blue-500 text-white'
        }`}>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-xs font-medium">
              {isCustomer ? '🙋‍♂️ Customer' : '🧑‍💼 Salesperson'}
            </span>
            <span className="text-xs opacity-75">
              {formatTime(transcript.timestamp)}
            </span>
          </div>
          <p className="text-sm">{transcript.text}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 bg-white border border-gray-200 rounded-lg shadow flex flex-col" style={{ minHeight: '130px' }}>
      {/* Header */}
      <div className="bg-gray-50 px-3 py-1 border-b border-gray-200 rounded-t-lg flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h2 className="text-sm font-semibold text-gray-800">Live Transcript</h2>
          </div>
          {/* <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setShowDebugInfo(!showDebugInfo);
                if (!showDebugInfo) {
                  dispatch(getTranscriptStats());
                }
              }}
              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            >
              {showDebugInfo ? 'Hide Debug' : 'Debug'}
            </button>
          </div> */}
        </div>
      </div>

      {/* Transcript Content - This is the only scrollable part */}
      <div
        ref={feedRef}
        className="flex-1 overflow-y-auto p-2"
        style={{ minHeight: 0, scrollBehavior: 'smooth' }}
      >
        {transcripts.length === 0 ? (
          isCustomerSpeaking ? (
            <div className="pt-4">
              <TypingIndicator />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="flex items-center gap-1.5 text-gray-400">
                <span className="text-sm">🎤</span>
                <span className="text-xs">No transcripts yet</span>
              </div>
            </div>
          )
        ) : (
          <div>
            {transcripts.map((transcript) => (
              <TranscriptMessage key={transcript.id} transcript={transcript} />
            ))}
            {isCustomerSpeaking && <TypingIndicator />}
            {/* Add margin bottom to ensure latest transcript is fully visible */}
            <div className="h-1" />
          </div>
        )}
      </div>

      {/* Debug Info */}
      {showDebugInfo && (
        <div className="bg-yellow-50 px-3 py-2 border-t border-yellow-200 rounded-b-lg flex-shrink-0">
          <div className="text-xs text-yellow-800">
            <div className="font-semibold mb-1">Debug Info:</div>
            <div>• Total transcripts: {transcripts.length}</div>
            <div>• Customer: {transcripts.filter(t => t.speaker === 'customer').length}</div>
            <div>• Salesperson: {transcripts.filter(t => t.speaker === 'salesperson').length}</div>
            <div>• Deduplication: Enhanced (checks last 5, similarity &gt;80%)</div>
            <div>• Check console for detailed logs</div>
          </div>
        </div>
      )}

      {/* Footer with count */}
      {transcripts.length > 0 && (
        <div className="bg-gray-50 px-6 py-2 border-t border-gray-200 rounded-b-2xl flex-shrink-0">
          <div className="text-sm text-gray-600 text-center">
            {transcripts.length} transcript{transcripts.length === 1 ? '' : 's'} recorded
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptFeed;